import { computed, inject } from "@angular/core";
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from "@ngrx/signals";
import { addEntity, setAllEntities, setEntities, updateEntity, withEntities } from "@ngrx/signals/entities";
import { ProviderService } from "../../services/provider/provider.service";
import { UserFacade } from "../user/user.facade";
import { exhaustMap, firstValueFrom, map, pipe, tap } from "rxjs";
import { extractErrorMessage } from "../../interceptors/helpers/extract-error-message.helper";
import { formatProviderApiError } from "../../interceptors/helpers/provider-conflict-error.helper";
import { ClinicLocationStore } from "../clinic/clinic-location.store";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tapResponse } from "@ngrx/operators";

export interface Provider {
    id: 1
    first_name: string
    last_name: string
    email_address: string
    npi: string
    provider_type: string
    primary_provider: number
    created_at: string
    updated_at: string
    active: boolean
    end_date: string
    onboarding_status: number,
    locations: any
    licence_expire_date?: string | null
    license_expire_date?: string | null
    insurance_expire_date?: string | null
}

interface ProviderStoreState {
    loading: boolean
    addProviderSuccess: boolean,
    activeId: number,
    activeClinicId: number,
    activeProviderId: number,
    activeClinicOnboardingState: number,
    active: boolean,
    providerStatus: string,
    error: {
        error: boolean, message: string
    },
    providersLoadingStatus: string
}

export const ProviderStore = signalStore(
    {providedIn: 'root'},
    withState<ProviderStoreState>({
        loading: false,
        error: {
            error: false, message: ''
        },
        activeId: 0,
        activeClinicId: 0,
        activeProviderId: 0,
        activeClinicOnboardingState: 0,
        active: false,
        addProviderSuccess: false,
        providersLoadingStatus: 'idle',
        providerStatus: 'idle'
    }),
    withEntities<Provider>(),
    withComputed((store) => ({
        providers: computed(() => Object.values(store.entities())),
        providerProviderId: computed(() => {
            const primary = store.entities().find((p:any) => p.primary_provider === 1);
            return primary ? primary.id : null;
        })
    })),
    withProps((store) => ({
        clinicLocationStore: inject(ClinicLocationStore),
        providerService: inject(ProviderService),
        userFacade: inject(UserFacade)
    })),
    withMethods((store) => ({
        clear() {
            patchState(store, {
                entityMap: {},
                ids: [],
            });
            patchState(store, {
                loading: false,
                error: {
                    error: false, message: ''
                },
                activeId: 0,
                activeClinicId: 0,
                activeProviderId: 0,
                activeClinicOnboardingState: 0,
                active: false,
                addProviderSuccess: false
            })
        },
        clearError() {
            patchState(store, {
                error: {
                    error: false,
                    message: ''
                }
            })
        },
        setError(error: boolean, message: string) {
            patchState(store, {
                error: {
                    error, message
                }
            })
        },
        setProvidersLoadingStatus(providersLoadingStatus: string) {
            patchState(store, { 
                providersLoadingStatus
            })
        },
        setLoading(loading: boolean) {
            patchState(store, { loading })
        },
        setProviderOnboardingStatus(provider_id: number, onboarding_status: number) {
            patchState(store,
                updateEntity({id: provider_id, changes: {onboarding_status}})
            )
        },
        updateProviderExpiryDatesLocal(provider_id: number, dto: { licence_expire_date?: string | null; insurance_expire_date?: string | null }) {
            patchState(store,
                updateEntity({
                    id: provider_id,
                    changes: {
                        licence_expire_date: dto.licence_expire_date ?? null,
                        license_expire_date: dto.licence_expire_date ?? null,
                        insurance_expire_date: dto.insurance_expire_date ?? null
                    }
                })
            )
        },
        async updateProviderExpiryDates(
            provider_id: number,
            dto: { licence_expire_date: string | null; insurance_expire_date: string | null },
            callback?: () => void
        ) {
            this.clearError()
            this.setLoading(true)
            try {
                const response: any = await firstValueFrom(
                    store.providerService.updateProviderExpiryDates(provider_id, dto).pipe(
                        map((response: any) => response)
                    )
                )

                const provider = response?.provider ?? response ?? {}

                patchState(store,
                    updateEntity({
                        id: provider_id,
                        changes: {
                            licence_expire_date: provider?.licence_expire_date ?? provider?.license_expire_date ?? dto.licence_expire_date ?? null,
                            license_expire_date: provider?.license_expire_date ?? provider?.licence_expire_date ?? dto.licence_expire_date ?? null,
                            insurance_expire_date: provider?.insurance_expire_date ?? dto.insurance_expire_date ?? null
                        }
                    })
                )

                callback?.()
            } catch(err) {
                this.setError(true, extractErrorMessage(err))
            } finally {
                this.setLoading(false)
            }
        },
        setActiveClinicId(activeClinicId: number) {
            patchState(store, {activeClinicId})
        },
        isPrimaryProvider(provider_id: number) {
            const provider = store.providers().find((p) => p.id === provider_id)
            return !!provider?.primary_provider
        },
        async addProvider(provider: Provider, callback: () => void) {
            this.clearError()
            this.setLoading(true)
            try {
                const newProvider = await firstValueFrom(
                    store.providerService.createProvider(provider).pipe(
                        map((response: any) => response)
                    )
                )
                patchState(
                    store,
                    addEntity(newProvider),
                  { activeProviderId: newProvider.id }
                )
                callback()
            } catch(err) {
                this.setError(true, formatProviderApiError(err, {
                    npi: provider.npi,
                    emailAddress: provider.email_address
                }))

            } finally {
                this.setLoading(false)
            }
        },
        async getProviderLocations(clinic_id: number, provider_id: number, callback?: (value ?: any) => void) {
            this.clearError()
            this.setLoading(true)
            try {
                const locations = await firstValueFrom(
                    store.providerService.getProviderLocations(clinic_id, provider_id).pipe(
                        map((response: any) => response)
                    )
                )

                patchState(store,
                    updateEntity({id: Number(provider_id), changes: {
                        locations: locations.clinic_provider_locations
                    }})
                )
                store.clinicLocationStore.setClinicLocations(locations.clinic_provider_locations)
            } catch(err) {
                this.setError(true, extractErrorMessage(err))
            } finally {
                this.setLoading(false)
            }
        },
        async updateProvider(provider: Provider, callback: () => void) {
            this.clearError()
            this.setLoading(true)
            try {
                const response: any = await firstValueFrom(
                    store.providerService.updateProvider(provider).pipe(
                        map((response: any) => response)
                    )
                )

                const updatedProvider = response?.provider ?? response ?? provider
                const providerId = Number(updatedProvider?.id ?? provider.id)

                patchState(store,
                    updateEntity({
                        id: providerId,
                        changes: {
                            ...provider,
                            ...updatedProvider,
                            id: providerId,
                            email_address: updatedProvider?.email_address ?? updatedProvider?.email ?? provider.email_address
                        }
                    })
                )
                callback()
            } catch(err) {
                this.setError(true, formatProviderApiError(err, {
                    npi: provider.npi,
                    emailAddress: provider.email_address
                }))
            } finally {
                this.setLoading(false)
            }
        },
        
        /***
            * Deletes a provider
            * @param hard_delete - use with caution, setting to true will trigger a hard delete in the database and the data can not be recovered
        */
        async deleteProvider(
            provider: Provider,
            callback: () => void,
            hard_delete = false,
            errorCallback?: (message: string) => void
        ) {
            this.clearError()
            this.setLoading(true)
            try {
                const update = await firstValueFrom(
                    store.providerService.deleteProvider(provider, hard_delete).pipe(
                        map((response: any) => response)
                    )
                )
                const providers = store.providers().filter((p) => p.id !== provider.id)
                patchState(store, setAllEntities(providers))
                callback()
            }  catch(err) {
                const message = extractErrorMessage(err)
                this.setError(true, message)
                errorCallback?.(message)
            }  finally {
                this.setLoading(false)
            }
        },

        /**
         * @note this method is deprecated and all areas using it need to convert to the 
         * new method getProvidersByClinicId
         */
        async getAllProvidersByClinic() {
            this.clearError()
            
            const providerService = inject(ProviderService)
            const facade = inject(UserFacade)
            const alreadyLoaded = Object.keys(store.entities()).length > 0;

            if (alreadyLoaded) return

            this.setLoading(true)
            try {
                const providers = await firstValueFrom(
                    store.providerService.getAllProidersByClinic(facade.clinic_id()).pipe(
                        map((response: any) => response)
                    )
                )
                patchState(store, setEntities(providers))
            } catch(err) {
                this.setError(true, extractErrorMessage(err))
            } finally {
                this.setLoading(false)
            }
        },
        setProviderStatus(providerStatus: string) {
            patchState(store, {providerStatus})
        },
        updateProviderOnboardingStatus(providerId: number, onboarding_status: number) {
            patchState(store, updateEntity({
                id: providerId,
                changes: {
                    onboarding_status
                }
            }));
        },
        updateProviderActiveStatus(providerId: number, active: boolean) {
            patchState(store, updateEntity({
                id: providerId,
                changes: {
                    active
                }
            }));
        }
    })),
    withMethods((store) => ({
        getProvidersSummaries: rxMethod<void>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setProvidersLoadingStatus('loading')
                }),
                exhaustMap(() => 
                    store.providerService.getProvidersSummaries().pipe(
                        tapResponse({
                            next: (response: any) => {
                                patchState(store, setEntities(response))
                                store.setProvidersLoadingStatus('success')
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setProvidersLoadingStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        getProvidersByClinicId: rxMethod<{clinic_id?:number, onSuccess?: (value?:any) => void}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setProvidersLoadingStatus('loading')
                }),
                map((params) => ({
                    ...params,
                    clinic_id: params.clinic_id ?? store.userFacade.clinic_id()
                })),
                exhaustMap(({clinic_id, onSuccess}) => 
                    store.providerService.getAllProidersByClinic(clinic_id).pipe(
                        tapResponse({
                            next: (response: any) => {
                                patchState(store, setAllEntities(response))
                                store.setActiveClinicId(clinic_id)
                                store.setProvidersLoadingStatus('success')
                                onSuccess?.(response)
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setProvidersLoadingStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        updateProviderInformation: rxMethod<{provider: any, onSuccess?:() => void }>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setProviderStatus('loading')
                }),
                exhaustMap(({provider, onSuccess}) => 
                    store.providerService.updateProvider(provider).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const updatedProvider = response?.provider ?? response ?? provider
                                const providerId = Number(updatedProvider?.id ?? provider.id)

                                patchState(store,
                                    updateEntity({
                                        id: providerId,
                                        changes: {
                                            ...provider,
                                            ...updatedProvider,
                                            id: providerId,
                                            email_address: updatedProvider?.email_address ?? updatedProvider?.email ?? provider.email_address
                                        }
                                    })
                                )
                                store.setProviderStatus('success')
                                onSuccess?.()
                            },
                            error: (err: unknown) => {
                                console.error(err);
                                store.setProviderStatus('error')
                                store.setError(true, formatProviderApiError(err, {
                                    npi: provider.npi,
                                    emailAddress: provider.email_address
                                }))
                            }
                        })
                    )
                )
            )
        ),
        activateProvider: rxMethod<{clinic_id: number, provider_id: number, onSuccess?: () => void}>(
            pipe(
                tap(() => {
                    store.clearError();
                    store.setLoading(true);
                }),
                exhaustMap(({clinic_id, provider_id, onSuccess}) => 
                    store.providerService.activateProvider(clinic_id, provider_id).pipe(
                        tapResponse({
                            next: () => {
                                store.setProviderStatus('success')
                                patchState(store, updateEntity({
                                    id: provider_id,
                                    changes: {
                                        active: true
                                    }
                                }));
                                onSuccess?.()
                            },
                            error: (err: unknown) => {
                                store.setProviderStatus('error')
                                store.setError(true, extractErrorMessage(err))
                            }
                        })
                    )
                )
            )
        ),
        deactivateProvider: rxMethod<{clinic_id: number, provider_id: number, onSuccess?: () => void}>(
            pipe(
                tap(() => {
                    store.clearError();
                    store.setLoading(true);
                }),
                exhaustMap(({clinic_id, provider_id, onSuccess}) => 
                    store.providerService.deactivateProvider(clinic_id, provider_id).pipe(
                        tapResponse({
                            next: () => {
                                store.setProviderStatus('success')
                                patchState(store, updateEntity({
                                    id: provider_id,
                                    changes: {
                                        active: false
                                    }
                                }));
                                onSuccess?.()
                            },
                            error: (err: unknown) => {
                                store.setProviderStatus('error')
                                store.setError(true, extractErrorMessage(err))
                            }
                        })
                    )
                )
            )
        )
    }))
)
